export enum ClusterStatus {
  Active = 'active',
  Normal = 'normal',
  Creating = 'creating',
  Deleting = 'deleting',
  Importing = 'importing',
  Initializing = 'initializing',
  Modifying = 'modifying',
  Recovering = 'recovering',
  Restoring = 'restoring',
  Resuming = 'resuming',
  Scaling = 'scaling',
  Upgrading = 'upgrading',
  Paused = 'paused',
  Hibernated = 'hibernated',
  Cleared = 'cleared',
  Deleted = 'deleted',
  Deletecomplete = 'deletecomplete',
  Unknown = ''
}
export const clusterStatusFilters = Object.values(ClusterStatus)
  .filter((s) => s !== '')
  .map((i) => ({ text: i, value: i }))

export enum ClusterDeployType {
  Dedicated = 'dedicated',
  Shared = 'shared',
  TiupCluster = 'tiup-cluster',
  TiDBOperator = 'tidb-operator',
  Byoc = 'byoc',
  ByocM1 = 'byoc-m1',
  Premium = 'premium',
  NgHost = 'nextgen-host',
  ByocHost = 'byoc-host'
}
export enum ClusterDeployTypeV2 {
  Starter = 'starter',
  Essential = 'essential',
  Dedicated = 'dedicated',
  Premium = 'premium',
  /** Treated like {@link Premium} across Clinic UI feature gates */
  PremiumReserved = 'premium reserved',
  /** Treated like {@link Premium} across Clinic UI feature gates */
  EssentialV2 = 'essential v2',
  PremiumResourcePool = 'premium resource pool',
  /** Same resource-pool semantics as {@link PremiumResourcePool} */
  PremiumReservedResourcePool = 'premium reserved resource pool',
  /** Same resource-pool semantics as {@link PremiumResourcePool} */
  EssentialV2ResourcePool = 'essential v2 resource pool',
  Byoc = 'byoc',
  ByocResourcePool = 'byoc resource pool',

  TiupCluster = 'tiup-cluster',
  TiDBOperator = 'tidb-operator'
}

/** deploy_type_v2 values that should behave the same as `premium` in the UI */
export const PREMIUM_LIKE_DEPLOY_TYPES_V2: readonly ClusterDeployTypeV2[] = [
  ClusterDeployTypeV2.Premium,
  ClusterDeployTypeV2.PremiumReserved,
  ClusterDeployTypeV2.EssentialV2
]

/** Premium-line resource pools (`deploy_type_v2`). */
export const PREMIUM_LINE_RESOURCE_POOL_DEPLOY_TYPES_V2: readonly ClusterDeployTypeV2[] =
  [
    ClusterDeployTypeV2.PremiumResourcePool,
    ClusterDeployTypeV2.PremiumReservedResourcePool,
    ClusterDeployTypeV2.EssentialV2ResourcePool
  ]

export function isPremiumLineResourcePoolDeployTypeV2(
  deployTypeV2?: string | null
): boolean {
  if (deployTypeV2 == null || deployTypeV2 === '') return false
  return (
    PREMIUM_LINE_RESOURCE_POOL_DEPLOY_TYPES_V2 as readonly string[]
  ).includes(deployTypeV2)
}

/**
 * Backend may send spaced / hyphen / underscore variants (e.g. `essential-v2`).
 * Matches {@link EssentialV2} without treating plain `essential` as v2.
 */
export function isPremiumLikeDeployTypeV2(
  deployTypeV2?: string | null
): boolean {
  if (deployTypeV2 == null || deployTypeV2 === '') return false
  const values = PREMIUM_LIKE_DEPLOY_TYPES_V2 as readonly string[]
  return values.includes(deployTypeV2)
}

/** Child clusters that show a “Resource pool” parent link. */
export function isResourcePoolTierChildDeployTypeV2(
  deployTypeV2?: string | null
): boolean {
  return (
    isPremiumLikeDeployTypeV2(deployTypeV2) ||
    deployTypeV2 === ClusterDeployTypeV2.Byoc
  )
}

/** Child clusters that show a “Master cluster” parent link. */
export function isMasterClusterTierChildDeployTypeV2(
  deployTypeV2?: string | null
): boolean {
  return (
    deployTypeV2 === ClusterDeployTypeV2.Starter ||
    deployTypeV2 === ClusterDeployTypeV2.Essential
  )
}
export const clusterDeployTypeFilters = Object.values(ClusterDeployTypeV2).map(
  (i) => ({ text: i, value: i })
)

export enum Provider {
  AWS = 'aws',
  Azure = 'azure',
  GCP = 'gcp',
  AliCloud = 'aliCloud'
}

export const providerFilters = [
  {
    text: 'AWS',
    value: 'aws'
  },
  {
    text: 'GCP',
    value: 'gcp'
  },
  {
    text: 'Azure',
    value: 'azure'
  },
  {
    text: 'AliCloud',
    value: 'aliCloud'
  }
]

export enum TenantPlan {
  OnDemand = 'on_demand',
  PoC = 'poc',
  FreeTrial = 'free_trial'
}
export const tenantPlanFilters = Object.values(TenantPlan).map((i) => ({
  text: i,
  value: i
}))

export enum ControlPlaneArch {
  GardenerArch = 'gardener',
  CloudNativeArch = 'cloudnative',
  ThreeLayerArch = '3layer'
}
export const controlPlaneArchFilters = Object.values(ControlPlaneArch).map(
  (i) => ({
    text: i,
    value: i
  })
)

const checkStatusFactory = (statuslist: ClusterStatus[], allow = true) => {
  return (status: ClusterStatus) => {
    const includes = statuslist.includes(status)
    return allow ? includes : !includes
  }
}

// status docs: https://pingcap.feishu.cn/wiki/wikcnkpzvDYxuCxpeDGwX2ImWeb#doxcnG8Uy0ICqGECSADkXlnScNg
export const actionPreflightMap = {
  gitops: checkStatusFactory(
    [
      ClusterStatus.Hibernated,
      ClusterStatus.Cleared,
      ClusterStatus.Deleted,
      ClusterStatus.Deletecomplete,
      ClusterStatus.Unknown
    ],
    false
  ),
  ctl: checkStatusFactory(
    [
      ClusterStatus.Paused,
      ClusterStatus.Hibernated,
      ClusterStatus.Cleared,
      ClusterStatus.Deleted,
      ClusterStatus.Deletecomplete,
      ClusterStatus.Unknown
    ],
    false
  ),
  tidbDashboard: checkStatusFactory(
    [
      ClusterStatus.Paused,
      ClusterStatus.Resuming,
      ClusterStatus.Creating,
      ClusterStatus.Deleting,
      ClusterStatus.Initializing,
      ClusterStatus.Hibernated,
      ClusterStatus.Cleared,
      ClusterStatus.Deleted,
      ClusterStatus.Deletecomplete,
      ClusterStatus.Unknown
    ],
    false
  ),
  opsPortal: checkStatusFactory(
    [
      ClusterStatus.Hibernated,
      ClusterStatus.Cleared,
      ClusterStatus.Deleted,
      ClusterStatus.Deletecomplete,
      ClusterStatus.Unknown
    ],
    false
  )
}

// ref doc: https://pingcap.feishu.cn/wiki/wikcnkpzvDYxuCxpeDGwX2ImWeb
const statusColorPreflightMap = {
  [ClusterStatus.Active]: 'green',
  [ClusterStatus.Normal]: 'green',
  [ClusterStatus.Paused]: 'orange',
  [ClusterStatus.Upgrading]: 'blue',
  [ClusterStatus.Resuming]: 'blue',
  [ClusterStatus.Creating]: 'blue',
  [ClusterStatus.Deleting]: 'blue',
  [ClusterStatus.Restoring]: 'blue',
  [ClusterStatus.Importing]: 'blue',
  [ClusterStatus.Modifying]: 'blue',
  [ClusterStatus.Initializing]: 'blue',
  [ClusterStatus.Scaling]: 'blue',
  [ClusterStatus.Recovering]: 'blue',
  [ClusterStatus.Hibernated]: 'red',
  [ClusterStatus.Cleared]: 'red',
  [ClusterStatus.Deleted]: 'red',
  [ClusterStatus.Deletecomplete]: 'red',
  [ClusterStatus.Unknown]: 'red'
}

export function getStatusColor(status: string | undefined) {
  let color = 'default'
  if (status !== undefined) {
    color = statusColorPreflightMap[status as ClusterStatus]
    if (color === undefined) {
      if (status.endsWith('ing')) {
        color = 'blue'
      } else {
        color = 'orange'
      }
    }
  }
  return color
}
